/**
 * Displacement panel — refugee and displacement flow events.
 *
 * Filters events where source === 'displacement' or tags include
 * 'displacement'/'refugee'. Falls back to keyword scanning.
 * Click → fly to origin location.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { getMap } from '@/map/engine';
import type { NormalizedEvent } from '@/sources/base-source';

const DISPLACEMENT_KEYWORDS = ['displacement', 'displaced', 'refugee', 'idp', 'internally displaced', 'asylum', 'migration crisis', 'forced migration'];

function isDisplacementEvent(ev: NormalizedEvent): boolean {
  if (ev.source === 'displacement') return true;
  if (ev.tags?.some(t => t === 'displacement' || t === 'refugee' || t === 'idp')) return true;
  const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
  return DISPLACEMENT_KEYWORDS.some(kw => text.includes(kw));
}

export function createDisplacementPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'displacement-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function extractPeopleCount(text: string): string | null {
    // Try to find numbers followed by people/persons/displaced/refugees
    const match = text.match(/(\d[\d,. ]*(?:\s*(?:million|mil|m|thousand|k))?\s*(?:people|persons|displaced|refugees|idps|individuals|families))/i);
    return match ? match[1].trim() : null;
  }

  function render(): void {
    const events = appStore.getState().events;
    const displacementEvents = events
      .filter(isDisplacementEvent)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    contentArea.textContent = '';

    // Summary header
    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${displacementEvents.length > 0 ? 'var(--severity-high)' : 'var(--text-muted)'};`;
    countNum.textContent = String(displacementEvents.length);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `displacement event${displacementEvents.length !== 1 ? 's' : ''}`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    if (displacementEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No displacement data available \u2014 source may need API key';
      contentArea.appendChild(msg);
      return;
    }

    // Feed header
    const feedHeader = document.createElement('div');
    feedHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    feedHeader.textContent = `Events (${displacementEvents.length})`;
    contentArea.appendChild(feedHeader);

    for (const ev of displacementEvents.slice(0, 60)) {
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary); border-left: 3px solid var(--severity-high);
        cursor: pointer;
      `;

      if (ev.location) {
        row.onclick = () => {
          const map = getMap();
          if (map && ev.location) {
            map.flyTo({
              center: [ev.location.lng, ev.location.lat],
              zoom: Math.max(map.getZoom(), 5),
              duration: 600,
            });
          }
        };
        row.title = 'Click to fly to location';
      }

      // Title
      const titleEl = document.createElement('div');
      titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); margin-bottom: 3px;';
      titleEl.textContent = ev.title;
      row.appendChild(titleEl);

      // Detail row: origin country, people count estimate
      const detailRow = document.createElement('div');
      detailRow.style.cssText = 'display: flex; align-items: center; gap: 6px; flex-wrap: wrap;';

      // Origin indicator
      if (ev.location?.country || ev.location?.countryCode) {
        const originBadge = document.createElement('span');
        originBadge.style.cssText = `
          font-size: 9px; padding: 1px 5px; border-radius: 3px;
          background: var(--bg-primary); color: var(--text-secondary);
          font-family: var(--font-mono);
        `;
        originBadge.textContent = ev.location.country ?? ev.location.countryCode ?? '';
        detailRow.appendChild(originBadge);
      }

      // People count if extractable
      const desc = ev.description ?? ev.title;
      const count = extractPeopleCount(desc);
      if (count) {
        const countBadge = document.createElement('span');
        countBadge.style.cssText = `
          font-size: 9px; padding: 1px 5px; border-radius: 3px;
          background: var(--severity-high); color: #fff;
        `;
        countBadge.textContent = count;
        detailRow.appendChild(countBadge);
      }

      // Source badge
      const srcBadge = document.createElement('span');
      srcBadge.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-left: auto;';
      srcBadge.textContent = ev.source;
      detailRow.appendChild(srcBadge);

      row.appendChild(detailRow);

      // Timestamp
      const time = document.createElement('div');
      time.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 3px;';
      time.textContent = relativeTime(ev.timestamp);
      row.appendChild(time);

      contentArea.appendChild(row);
    }
  }

  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
